import dayjs, { Dayjs } from 'dayjs';
import minMax from 'dayjs/plugin/minMax';

import {
    BankAccount,
    BankAccountStatus,
} from 'services/hurdlr/queries/useHurdlrPlaidItems/useHurdlrPlaidItems.interface';

dayjs.extend(minMax);

/**
 * Expenses and revenue sync on separate schedules, so the later of the two is the last time the
 * account produced anything at all.
 */
export function getLastAccountSyncDate(account: BankAccount): Dayjs | null {
    const syncedDates = [account.lastExpenseSyncedDate, account.lastRevenueSyncedDate]
        .filter((date): date is string => Boolean(date))
        .map((date) => dayjs(date))
        .filter((date) => date.isValid());

    if (syncedDates.length === 0) {
        return null;
    }

    return dayjs.max(syncedDates);
}

type AccountSyncState = 'active' | 'notSyncing' | 'paused';

/**
 * `INACTIVE` covers two unrelated situations. Removing an account clears `syncTransactions` in the
 * same call, so the pair separates a deliberate opt-out from an account that stopped on its own.
 */
export function getAccountSyncState(account: BankAccount): AccountSyncState {
    // Hurdlr types every field optional; `=== false` so an absent flag isn't read as an opt-out.
    if (account.syncTransactions === false) {
        return 'paused';
    }

    // Match on ACTIVE, not on the states that aren't — an unrecognised status must never read as
    // a healthy account.
    if (account.status === BankAccountStatus.Active) {
        return 'active';
    }

    return 'notSyncing';
}
